import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { kycAPI } from '../services/api';
import { Upload, XCircle, AlertCircle, FileText, Home, CreditCard, FileCheck, User } from 'lucide-react';

const KycPage: React.FC = () => {
  const { user, updateUser, loading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [kycStatus, setKycStatus] = useState<string>('not_submitted');
  const [kycRejectionReason, setKycRejectionReason] = useState<string>('');
  
  const [documents, setDocuments] = useState<Array<{ type: string; file: File | null }>>([
    { type: 'id_proof', file: null }, // Aadhar
    { type: 'driver_license', file: null }, // Driving License
    { type: 'passport_photo', file: null } // Passport Photo
  ]);
  const [uploading, setUploading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // Early redirect if user is already verified
  useEffect(() => {
    if (authLoading) {
      return;
    }

    // If user is already verified, redirect to home immediately
    if (user?.kycStatus === 'approved') {
      navigate('/', { replace: true });
      return;
    }

    const userId = user?._id || user?.id;
    
    if (user && userId) {
      fetchKycStatus();
    } else {
      // Auth finished loading but no user - stop loading
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  // Safety timeout - if loading takes too long, show the form anyway
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading && !authLoading) {
        setLoading(false);
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timeout);
  }, [loading, authLoading]);

  // Prevent browser back button navigation when KYC is required
  useEffect(() => {
    if (!user || authLoading || loading) return;

    const needsPhone = !user.phone || user.phone.trim() === '';
    const kycStatus = user.kycStatus || 'not_submitted';
    const needsKyc = user.role === 'provider' && (kycStatus === 'not_submitted' || kycStatus === 'rejected');

    // Only block back button if KYC is required
    if (needsKyc || needsPhone) {
      // Push current state to prevent back navigation
      window.history.pushState(null, '', window.location.href);

      const handlePopState = (event: PopStateEvent) => {
        // Prevent back navigation
        window.history.pushState(null, '', window.location.href);
        alert('Please complete your KYC verification before accessing other parts of the app.');
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [user, authLoading, loading]);

  const fetchKycStatus = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await kycAPI.getMyKycStatus();
      if (response.success && response.data) {
        setKycStatus(response.data.kycStatus || 'not_submitted');
        if (response.data.kycRejectionReason) {
          setKycRejectionReason(response.data.kycRejectionReason);
        }
        // Documents are stored on server, we don't need to load them for file upload
      } else {
        // If no data, default to not_submitted
        setKycStatus('not_submitted');
      }
    } catch (err: any) {
      console.error('Error fetching KYC status:', err);
      // If error, default to not_submitted so user can still submit
      setKycStatus('not_submitted');
      // Don't show error for 404 or 401 (user might not have submitted yet or not authenticated)
      // Only show error for other server errors
      if (err.response?.status && err.response.status !== 404 && err.response.status !== 401) {
        setError('Failed to load KYC status. You can still submit your documents.');
      }
    } finally {
      // Always stop loading, even on error
      setLoading(false);
    }
  };


  const handleFileChange = (index: number, file: File | null) => {
    const updated = [...documents];
    updated[index] = { ...updated[index], file };
    setDocuments(updated);
  };


  const uploadKycDocument = async (file: File, documentType: string): Promise<string> => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);

    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/kyc/upload-document`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to upload document');
    }

    const data = await response.json();
    return data.data.url;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate documents - Aadhar, Driving License, and Passport Photo are required
    const idProof = documents.find(doc => doc.type === 'id_proof');
    const driverLicense = documents.find(doc => doc.type === 'driver_license');
    const passportPhoto = documents.find(doc => doc.type === 'passport_photo');
    
    if (!idProof?.file || !driverLicense?.file || !passportPhoto?.file) {
      setError('Please upload Aadhar, Driving License, and Passport Photo documents');
      return;
    }

    try {
      setSubmitting(true);
      setUploading(true);
      
      // Upload each document individually to get URLs
      const idProofUrl = await uploadKycDocument(idProof.file, 'id_proof');
      const driverLicenseUrl = await uploadKycDocument(driverLicense.file, 'driver_license');
      const passportPhotoUrl = await uploadKycDocument(passportPhoto.file, 'passport_photo');
      
      // Submit KYC with document URLs
      const response = await kycAPI.submitKyc([
        { type: 'id_proof', url: idProofUrl },
        { type: 'driver_license', url: driverLicenseUrl },
        { type: 'passport_photo', url: passportPhotoUrl }
      ]);
      
      if (response.success) {
        setSuccess('KYC documents submitted successfully! We will review them shortly.');
        setKycStatus('pending');
        // Update user context
        if (updateUser) {
          await updateUser();
        }
        // After KYC submission, redirect to complete profile for police verification
        navigate('/complete-profile', { replace: true });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to submit KYC documents');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'id_proof':
        return <CreditCard className="h-5 w-5" />;
      case 'address_proof':
        return <Home className="h-5 w-5" />;
      case 'driver_license':
        return <FileCheck className="h-5 w-5" />;
      case 'passport_photo':
        return <User className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  const getDocumentLabel = (type: string) => {
    switch (type) {
      case 'id_proof':
        return 'Aadhar Card';
      case 'address_proof':
        return 'Address Proof (Utility Bill, Bank Statement, etc.)';
      case 'driver_license':
        return 'Driving License';
      case 'passport_photo':
        return 'Passport Photo';
      default:
        return 'Other Document';
    }
  };

  // If already approved, refresh user and redirect to home
  useEffect(() => {
    if (kycStatus === 'approved' && !redirecting && !loading && !authLoading) {
      setRedirecting(true);
      // Refresh user data to ensure context is updated with latest KYC status
      const refreshAndNavigate = async () => {
        try {
          // Use refreshUser if available, otherwise updateUser
          if (refreshUser) {
            await refreshUser();
          } else if (updateUser) {
            await updateUser();
          }
          // Small delay to ensure state is updated before navigation
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 300);
        } catch (error) {
          console.error('Error refreshing user after KYC approval:', error);
          // Even if refresh fails, still navigate after a delay
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 300);
        }
      };
      
      refreshAndNavigate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycStatus, loading, authLoading]);

  // Show loading state while auth is loading, KYC status is being fetched, or redirecting
  // Also redirect if user is already verified (check both user object and fetched status)
  if (authLoading || loading || redirecting) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-300">
            {redirecting ? 'KYC approved! Redirecting...' : 'Loading KYC status...'}
          </p>
        </div>
      </div>
    );
  }

  // Early redirect check: if user is already verified, redirect immediately
  if (!authLoading && user?.kycStatus === 'approved') {
    // Trigger redirect in useEffect, but show loading state here
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-300">You are already verified. Redirecting to home...</p>
        </div>
      </div>
    );
  }

  // Early return if auth finished loading but no user (should be handled by ProtectedRoute, but safety check)
  // This is especially important in incognito mode where localStorage might be empty
  if (!authLoading && !user) {
    // Don't render anything here - let ProtectedRoute handle the redirect
    // But show a minimal loading state to prevent blank screen
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-300">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // If pending review
  if (kycStatus === 'pending') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">KYC Under Review</h2>
          <p className="text-gray-600 mb-6">
            Your KYC documents are currently being reviewed. We'll notify you once the review is complete.
          </p>
          <button
            onClick={() => navigate('/')}
            className="w-full px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // Safety check: if no user, show error message
  if (!user && !authLoading && !loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Please log in to access KYC verification.</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <Upload className="h-12 w-12 text-black mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Complete Your KYC Verification</h2>
            <p className="text-gray-600">
              {user?.role === 'provider' 
                ? 'Verified providers get more bookings and customer trust'
                : 'Get verified to unlock all features and build trust'}
            </p>
          </div>

          {kycStatus === 'rejected' && kycRejectionReason && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <XCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-red-900 mb-1">Previous Submission Rejected</h3>
                  <p className="text-sm text-red-700">{kycRejectionReason}</p>
                  <p className="text-sm text-red-600 mt-2">Please resubmit with correct documents.</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-600">{success}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Documents</h3>
              <p className="text-sm text-gray-600 mb-4">
                Please upload clear images or PDFs of your documents. Aadhar Card, Driving License, and Passport Photo are required.
              </p>

              {documents.map((doc, index) => (
                <div key={index} className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center text-gray-700">
                      {getDocumentIcon(doc.type)}
                      <span className="ml-2 font-medium">{getDocumentLabel(doc.type)}</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {doc.type === 'id_proof' ? 'Aadhar Card' : doc.type === 'driver_license' ? 'Driving License' : doc.type === 'passport_photo' ? 'Passport Photo' : 'Document'}
                      </label>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          handleFileChange(index, file);
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-white file:text-gray-800 hover:file:bg-gray-100"
                        required={doc.type === 'id_proof' || doc.type === 'driver_license' || doc.type === 'passport_photo'}
                      />
                      {doc.file && (
                        <p className="mt-2 text-sm text-green-600">
                          Selected: {doc.file.name} ({(doc.file.size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Accepted formats: JPG, PNG, PDF (Max 10MB)
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-start space-x-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Important Notes:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Documents should be clear and readable</li>
                  <li>All personal information should be visible</li>
                  <li>Passport photo must be a recent, clear photo</li>
                  <li>Review typically takes 24-48 hours</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting || uploading}
                className="px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : submitting ? 'Submitting...' : 'Submit for Review'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Add error boundary fallback
KycPage.displayName = 'KycPage';

export default KycPage;

